import React, { useCallback, useMemo, useState } from 'react';
import { DrawerV2, DrawerV2Props, Flex, LinkV1, TextV1, BoxProps, Divider, Box } from '@withjoy/joykit';
import { StyledFlex, StyledShopBox, StyledTopBar, storefrontNavigatorPlacementStyles } from './SidebarNav.styles';
import { ReactComponent as UpChevron } from '@assets/icons/up-chevron.svg';
import { SidebarNavMobileDeparmentProps, SidebarNavMobileDepartment } from './SidebarNavMobileDepartment';
import { SidebarNavMobileCategory, SidebarNavMobileCategoryProps } from './SidebarNavMobileCategory';
import { CURATION_DEPARTMENT_ICON } from '../../Catalog.constants';
import { useCatalogRegistryTranslations, useCurationsRegistryTranslations } from '../../Catalog.i18n';
import { CatalogDepartmentDictionaryEntry, CatalogDepartment } from '../../state/Catalog/Catalog.types';
import { useCatalogContext } from '../../state/Catalog';
import { useCatalogDepartments } from '../../hooks/useCatalogDepartments/useCatalogDepartments';
import { CoreCatalogCategoryFieldsFragment } from '@graphql/generated';
import { ReactComponent as DownChevron } from '@assets/icons/down-chevron.svg';
import { useRouterHelper, useTranslation } from '@shared/core';
import { StorefrontNavigator } from '../StorefrontNavigator';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useCatalogLoggingContext } from '../../Catalog.logging';

type CategoryListProps = ReadonlyArray<{
  id: string;
  title: string;
  icon: string;
  handleOnClick: () => void;
  component: React.FC<SidebarNavMobileCategoryProps> | React.FC<SidebarNavMobileDeparmentProps>;
}>;

const DRAWER_OVERRIDES: DrawerV2Props['overrides'] = {
  Content: {
    props: {
      opacity: '1 !important'
    }
  }
};

export const CatalogShopMenuMobile: React.FC<Omit<BoxProps, '__css'>> = props => {
  const { t } = useTranslation('catalogRegistry');
  const { getSidebarNavTranslations } = useCatalogRegistryTranslations();
  const { showAll, back, shop, departmentTitle } = getSidebarNavTranslations();
  const { isSidebarNavMobileOpen, toggleSidebarNavMobile, goToCategory, goToRootCurations, selectedCategory } = useCatalogContext();
  const { trackCategoryNavigateAction, trackExpandMobileShopNavAction } = useCatalogLoggingContext();

  const { departments, getDepartmentByIdOrPath, categoryHasChildren } = useCatalogDepartments({ selectedCategory });
  const { isAdminRoute } = useRouterHelper();
  const isBabyRegistryEnabled = useFeatureValue('enableBabyRegistrySoftLaunchFeatures');

  const shouldShowStorefrontNavigator = isBabyRegistryEnabled && !isAdminRoute;
  const [currentCategory, setCurrentCategory] = useState<CatalogDepartmentDictionaryEntry | null>(null);

  const { getCurationsTranslations } = useCurationsRegistryTranslations();
  const { departmentTitle: curationsDepartmentTitle } = getCurationsTranslations();

  const closeSidebarNavAndGo = useCallback(
    category => {
      setCurrentCategory(null);
      toggleSidebarNavMobile();
      goToCategory(category.publicUrlPath);
    },
    [toggleSidebarNavMobile, goToCategory]
  );

  const closeSidebarNav = () => {
    setCurrentCategory(null);
    toggleSidebarNavMobile();
  };

  const goBack = () => {
    const parentIndex = (currentCategory?.lineageCsp || []).length - 1;
    const publicUrlPath = parentIndex <= 0 ? null : currentCategory?.lineageCsp?.[parentIndex]?.publicUrlPath;

    if (publicUrlPath) {
      const category = getDepartmentByIdOrPath(publicUrlPath);
      setCurrentCategory(category);
    } else {
      setCurrentCategory(null);
    }
  };

  const handleProductCatalogCategoryItemOnClick = useCallback(
    (productCatalogCategoryItem: CoreCatalogCategoryFieldsFragment) => {
      if ((currentCategory?.lineageCsp || []).length > 1) {
        trackCategoryNavigateAction({ category: productCatalogCategoryItem, navigationSource: 'mobileShopMenu' });
        closeSidebarNavAndGo(productCatalogCategoryItem);
      } else {
        // This is when user is clicking into a category to view children -- not items.
        trackExpandMobileShopNavAction(productCatalogCategoryItem, currentCategory);
      }

      const dictCategory = getDepartmentByIdOrPath(productCatalogCategoryItem.publicUrlPath);
      setCurrentCategory(dictCategory);
    },
    [getDepartmentByIdOrPath, currentCategory, trackCategoryNavigateAction, closeSidebarNavAndGo, setCurrentCategory, trackExpandMobileShopNavAction]
  );

  const handleCurationsDepartmentOnClick = useCallback(() => {
    goToRootCurations();
  }, [goToRootCurations]);

  const title = currentCategory?.title || departmentTitle;

  const showChevron = (currentCategory?.lineageCsp || []).length < 2;

  const categoryList: CategoryListProps = useMemo(() => {
    const productCatalogCategoryList = categoryHasChildren(currentCategory) ? currentCategory.children : (departments as ReadonlyArray<CatalogDepartment>);

    return productCatalogCategoryList.map(productCatalogCategoryItem => {
      let handleOnClick;
      if (productCatalogCategoryItem.title !== curationsDepartmentTitle) {
        handleOnClick = () => handleProductCatalogCategoryItemOnClick(productCatalogCategoryItem as CatalogDepartmentDictionaryEntry);
      } else {
        // close nav bar in the case that a user clicks curations after it is already selected
        handleOnClick = () => {
          toggleSidebarNavMobile();
          handleCurationsDepartmentOnClick();
        };
      }

      const isCurations = productCatalogCategoryItem.id === curationsDepartmentTitle;

      return {
        id: productCatalogCategoryItem.id,
        title: productCatalogCategoryItem.title,
        icon: isCurations ? CURATION_DEPARTMENT_ICON : productCatalogCategoryItem.thumbnail?.url,
        handleOnClick,
        component: currentCategory ? SidebarNavMobileCategory : SidebarNavMobileDepartment
      };
    });
  }, [
    currentCategory,
    departments,
    curationsDepartmentTitle,
    handleProductCatalogCategoryItemOnClick,
    handleCurationsDepartmentOnClick,
    categoryHasChildren,
    toggleSidebarNavMobile
  ]);

  return (
    <>
      <StyledShopBox {...props}>
        <Flex
          justifyContent="center"
          alignItems="center"
          flexDirection="row"
          onClick={() => {
            trackExpandMobileShopNavAction(null);
            toggleSidebarNavMobile();
          }}
          padding="24px 12px"
        >
          <TextV1 marginRight={2}>{t('sidebarNav', 'shop')()}</TextV1> {<DownChevron />}
        </Flex>
      </StyledShopBox>

      <DrawerV2 isOpen={isSidebarNavMobileOpen} disableAutoFocus overrides={DRAWER_OVERRIDES} anchor="top" size={1}>
        {shouldShowStorefrontNavigator && (
          <Box __css={storefrontNavigatorPlacementStyles}>
            <StorefrontNavigator colorVariant="dark" />
          </Box>
        )}
        <Flex justifyContent="space-between" alignItems="center">
          <StyledTopBar>
            <StyledTopBar.Header backIcon={false} title={title}></StyledTopBar.Header>
          </StyledTopBar>
          <StyledShopBox padding="25px" onClick={closeSidebarNav}>
            <Flex justifyContent="center" alignItems="center" flexDirection="row">
              <TextV1 marginRight={2}>{shop}</TextV1>
              <UpChevron />
            </Flex>
          </StyledShopBox>
        </Flex>
        <Divider />
        <StyledFlex flexDirection="column" padding="22px 16px" marginBottom="50px">
          {currentCategory && currentCategory.lineageCsp?.length > 0 ? (
            <>
              <LinkV1 underline="none" marginBottom="28px" onClick={goBack}>
                {back}
              </LinkV1>
              <SidebarNavMobileCategory
                handleOnClick={() => {
                  if (currentCategory) {
                    trackCategoryNavigateAction({ category: currentCategory, navigationSource: 'mobileShopMenu' });
                    closeSidebarNavAndGo(currentCategory);
                  }
                }}
                title={showAll(title)}
                isBoldFont={true}
              />
            </>
          ) : undefined}
          {categoryList.map(category => (
            <category.component
              key={category.id}
              title={category.title}
              icon={category.icon}
              handleOnClick={category.handleOnClick}
              showChevron={category.id !== curationsDepartmentTitle && showChevron}
            />
          ))}
        </StyledFlex>
      </DrawerV2>
    </>
  );
};
